/**
 * opd-voice.js — OPD's voice / STT client.
 *
 * Provider: Sarvam Saaras STT (owner order 2026-08-08: "We are not using
 * Deepgram. We will be using Sarvam everywhere. Remove Deepgram."). This file
 * replaces the previous Deepgram-streaming engine while keeping the SAME
 * public contract every call site already uses (ConsultLive.jsx,
 * IntakeSteps.jsx, CommandAgent.jsx, command-dock.jsx, QuickPhoto.jsx,
 * CaptureDock.jsx, GlobalAgentButton.jsx) — none of them change:
 *
 *   • live waveform from AnalyserNode.getByteFrequencyData() (unchanged)
 *   • mic states (idle / recording / paused / processing / done)
 *   • pulse ring animation hooks (driven by RAF when level > threshold)
 *   • a "final" transcript segment per Sarvam VAD-bounded utterance
 *   • authenticated batch transcription when the real-time relay is
 *     unavailable
 *
 * WHY THE ENGINE CHANGED, NOT JUST THE PROVIDER NAME: Deepgram let the
 * browser open a WebSocket directly to Deepgram, carrying a short-lived
 * token this file minted via /api/v1/stt/deepgram-token. Sarvam has no such
 * ephemeral-token grant API — the only credential is a static subscription
 * key, which must never reach the browser (AGENTS §2). So the real-time leg
 * is now a server-side RELAY (server/opd/voice-stream.mjs, proven by the
 * identical wire protocol already shipping in the Gemma×Cerebras kiosk —
 * server/opd/kiosk-voice.mjs): this file opens a WebSocket to OUR OWN
 * server, which holds the Sarvam key and forwards audio/transcripts.
 *
 * That relay speaks raw 16 kHz PCM, not the WebM/Opus MediaRecorder blobs
 * Deepgram's WS accepted — so capture switched from MediaRecorder to Web
 * Audio (lib/audio.js's MicCapture, the same primitive the kiosk already
 * uses in production, incl. on iOS Safari). This is a genuine simplification,
 * not just a swap: MediaRecorder's WebM-vs-iOS-mp4 branching (recContainer,
 * useStreaming toggling for iOS) is GONE — Web Audio capture is uniform
 * across every browser, so there is no more browser-specific STREAMING vs
 * BATCH split; every browser streams.
 *
 * DIARIZATION MODEL (rebuilt 2026-08-08, same day the "no diarization" call
 * was corrected — Sarvam DOES diarize, on its Batch API only, and that
 * "batch" job is seconds-fast; see server/stt/sarvam.mjs): the live stream
 * still carries no speaker signal, so every live "final" below has
 * `dgSpeaker: null` and roleResolver.resolve() falls back to `defaultRole` —
 * the UI's own speaker toggle — exactly as before. What's new: for diarizing
 * sessions (sessionType 'opd-consult', or an explicit `diarize: true`),
 * stop() sends the full recording through one batch-diarization call and
 * returns `diarizedTurns` — clinical turns ({role, speaker, text, startMs,
 * endMs}) mapped by the canonical @healther/clinical-core/speech-turns.js
 * rule (first distinct voice = Doctor; <2 voices ⇒ empty, live toggle stays
 * authoritative). Deepgram gave attribution DURING the session; Sarvam gives
 * it seconds AFTER Stop. The consult UI (ConsultLive.jsx) swaps its
 * voice-turn attribution to `diarizedTurns` before the verbatim save.
 *
 * ALSO GONE: Sarvam's streaming STT is segmented at VAD (voice-activity
 * detection) boundaries, not continuously decoded — there is no
 * intra-utterance word-by-word interim transcript the way Deepgram's
 * `is_final:false` gave one. onInterim is therefore never called by the
 * streaming path in this file (kept in the public contract as a no-op for
 * callers that still pass it — the live waveform from onLevel is the
 * "never hide speech-to-text feedback" signal now, plus each completed
 * utterance still lands as a "final" within roughly a sentence's worth of
 * latency, same cadence Deepgram's UtteranceEnd-triggered finals gave).
 *
 * Exposed:
 *
 *   const session = OPDVoice.create({
 *     encounterId, sessionType: 'opd-intake'|'opd-consult',
 *     speaker: 'Doctor'|'Patient'|'Family'|'Child',
 *     onLevel: (n01) => …,          // 0..1, ~60Hz from RAF
 *     onInterim: (text) => …,       // never fires from the streaming path (see above)
 *     onFinal: (text, meta) => …,   // one call per Sarvam VAD-bounded utterance
 *     onState: (state) => …,        // idle|recording|paused|processing|done|error
 *     onUtteranceEnd: () => …,      // fires on Sarvam's END_SPEECH VAD signal —
 *                                   //   patient paused. Optional; does NOT stop
 *                                   //   the recorder, just signals.
 *     onSilence: () => …,           // alias of onUtteranceEnd (same fire points)
 *     onError: (err) => …,
 *   });
 *
 * onFinal meta CONTRACT (the consult UI reads meta.speaker / meta.role):
 *
 *   {
 *     confidence: null,             // Sarvam's STT response carries none
 *     speaker:    string,           // role LABEL — 'Doctor'|'Patient'|'Family'|'Child'
 *     role:       string,           // canonical lowercase — 'doctor'|'patient'|'family'|'child'
 *     dgSpeaker:  null,             // always null — see the diarization gap above
 *     diarized:   false,            // always false — see above
 *     fallback?:  'authenticated-batch', // present only on batch STT
 *   }
 *
 *   await session.start();
 *   session.pause();
 *   session.resume();
 *   await session.stop();           // returns full transcript + audio meta
 */
import { apiUrl, wsUrl } from './net-base.js';
import { authHeader } from './medplum-client.js';
import { MicCapture, base64ToBytes } from './audio.js';
import { utterancesToClinicalTurns } from '@healther/clinical-core/speech-turns.js';

(function (g) {
  'use strict';

  const VOICE_STREAM_WS_PATH = '/api/v1/stt/stream';

  // `surface` is kept in the call signature for back-compat with the three
  // prewarm call sites (voice-capture.jsx, ConsultLive.jsx, IntakeSteps.jsx)
  // even though the server no longer keys anything off it (Sarvam has no
  // keyterm-boost equivalent on this endpoint — see clinical-dictionary.mjs's
  // getKeytermsForSurface, now unused by this path, and DEFERRED.md).
  async function getSarvamTicket(_surface, magicLinkToken = '') {
    const r = await fetch(apiUrl('/api/v1/stt/sarvam-ticket'), { headers: voiceCredentialHeaders(magicLinkToken) });
    if (r.status === 503) {
      const e = new Error('STT_NOT_CONFIGURED');
      e.code = 'NOT_CONFIGURED';
      throw e;
    }
    if (!r.ok) throw new Error('sarvam-ticket failed: ' + r.status);
    return r.json();
  }

  function voiceCredentialHeaders(magicLinkToken = '') {
    const headers = { ...authHeader() };
    if (magicLinkToken) headers['X-Healther-Magic-Link'] = magicLinkToken;
    return headers;
  }

  // Batch STT (two uses): the fallback when the real-time relay is
  // unavailable/yielded no finals, and — with `diarize: true` — the
  // POST-SESSION speaker re-attribution of the multi-party consult room
  // (Sarvam's Batch API is the only surface that diarizes; the relay stream
  // carries no speaker signal — see server/stt/sarvam.mjs's header). The
  // client always sends a WAV file — capture is uniform Web Audio PCM across
  // every browser, so there is no more webm-vs-iOS-mp4 branching here the way
  // the MediaRecorder engine needed.
  async function transcribeRecordedBlob(audioBlob, { magicLinkToken = '', sessionType = 'opd-intake', speaker = 'Patient', diarize = false, numSpeakers = 0 } = {}) {
    const form = new FormData();
    form.append('audio', audioBlob, 'capture.wav');
    if (diarize) {
      form.append('diarize', '1');
      if (numSpeakers) form.append('num_speakers', String(numSpeakers));
    }
    const response = await fetch(apiUrl('/api/v1/stt/transcribe'), {
      method: 'POST',
      headers: voiceCredentialHeaders(magicLinkToken),
      body: form,
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(body.error || `batch transcription failed: ${response.status}`);
      error.code = 'STT_BATCH_FAILED';
      error.status = response.status;
      throw error;
    }
    return {
      transcript: String(body.transcript || '').trim(),
      utterances: Array.isArray(body.utterances) ? body.utterances : [],
      durationMs: Number(body.durationMs) || 0,
      confidence: Number.isFinite(body.confidence) ? body.confidence : null,
      sessionType,
      speaker,
    };
  }

  // ── WAV assembly from raw 16 kHz mono PCM16 chunks ─────────────────────
  // The batch-fallback blob AND the review-playback blob (IntakeSteps.jsx's
  // SpeakDone <audio> element) are both built from the same accumulated PCM
  // this session already captured for streaming — one audio pipeline, no
  // separate "recording for playback" path the way MediaRecorder was.
  function pcmChunksToWavBlob(chunks, sampleRate = 16000) {
    let totalBytes = 0;
    for (const c of chunks) totalBytes += c.length;
    const dataLen = totalBytes;
    const buf = new ArrayBuffer(44 + dataLen);
    const v = new DataView(buf);
    const str = (off, s) => { for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i)); };
    str(0, 'RIFF'); v.setUint32(4, 36 + dataLen, true); str(8, 'WAVE'); str(12, 'fmt ');
    v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
    v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * 2, true);
    v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, dataLen, true);
    let off = 44;
    const out = new Uint8Array(buf);
    for (const c of chunks) { out.set(c, off); off += c.length; }
    return new Blob([buf], { type: 'audio/wav' });
  }

  // ── Near-duplicate transcript dedup (kept from the Deepgram engine) ─────
  // Sarvam emits one transcript per VAD segment rather than re-finalizing
  // the same audio the way Deepgram occasionally did, so this guard is less
  // likely to trigger — but it is a cheap, harmless safety net against any
  // provider hiccup that re-sends a segment, so it stays.
  function normalizeForDup(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[.,!?;:'"()[\]]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  function tokensOf(s) {
    return normalizeForDup(s).split(' ').filter(Boolean);
  }
  // Pull out numeric tokens (doses, vitals, frequencies). If two lines
  // carry DIFFERENT numbers we never merge them — "paracetamol 500" and
  // "paracetamol 650" are distinct clinical facts, not an ASR retry.
  function numbersOf(s) {
    const m = normalizeForDup(s).match(/\d+(?:\.\d+)?/g);
    return m ? m.slice().sort() : [];
  }
  function sameNumbers(a, b) {
    const na = numbersOf(a), nb = numbersOf(b);
    if (na.length !== nb.length) return false;
    return na.every((v, i) => v === nb[i]);
  }
  function isNearDuplicate(prev, next) {
    const a = normalizeForDup(prev);
    const b = normalizeForDup(next);
    if (!a || !b) return false;
    // OPD safety guard: different numbers ⇒ never a duplicate.
    if (!sameNumbers(prev, next)) return false;
    // 1) exact match after normalization.
    if (a === b) return true;
    // 2) substring containment when either side is reasonably long
    //    (catches truncated stale finals mid-sentence).
    if ((a.length >= 12 || b.length >= 12) && (a.includes(b) || b.includes(a))) return true;
    // 3) token Jaccard ≥ 0.7 on the full strings.
    const ta = new Set(tokensOf(a));
    const tb = new Set(tokensOf(b));
    if (!ta.size || !tb.size) return false;
    let inter = 0;
    ta.forEach((t) => { if (tb.has(t)) inter++; });
    const union = ta.size + tb.size - inter;
    return union > 0 && inter / union >= 0.7;
  }

  // ── Speaker diarization → clinical role ──────────────────────────
  // See the DIARIZATION GAP note in the file header: Sarvam supplies no
  // per-utterance speaker index, so `resolve()` below is always called with
  // dgIndex === null in practice and always returns defaultRole. This
  // machinery is kept intact (not deleted) so a future diarization source
  // can plug back in without re-deriving the role-order convention.
  //
  // ROLE_LABEL is the human-facing label the UI bubbles show; the canonical
  // lowercase role is what downstream code keys on.
  const ROLE_ORDER = ['doctor', 'patient', 'family', 'child'];
  const ROLE_LABEL = { doctor: 'Doctor', patient: 'Patient', family: 'Family', child: 'Child' };

  // Normalize a UI-supplied speaker hint ('Doctor'|'Patient'|'Family'|'Child',
  // any case) to a canonical role. Defaults to 'patient' for unknown values so
  // a stray label never silently becomes a clinician.
  function roleFromLabel(label) {
    const k = String(label || '').trim().toLowerCase();
    if (ROLE_LABEL[k]) return k;            // already canonical
    if (k.includes('doctor') || k.includes('dr ') || k.startsWith('dr')) return 'doctor';
    if (k.includes('family') || k.includes('relative') || k.includes('attendant') || k.includes('caregiver')) return 'family';
    if (k.includes('child') || k.includes('kid') || k.includes('son') || k.includes('daughter')) return 'child';
    if (k.includes('patient')) return 'patient';
    return 'patient';
  }

  // Per-session diarization→role resolver. `defaultRole` is the UI toggle's
  // role, used when a final has no diarization index (currently: always).
  function createRoleResolver(defaultRole) {
    const byIndex = new Map();          // dgSpeaker index → canonical role
    const used = new Set();
    let firstSeen = false;
    function claim(role) {
      if (!used.has(role)) { used.add(role); return role; }
      for (const r of ROLE_ORDER) { if (!used.has(r)) { used.add(r); return r; } }
      return 'family';
    }
    return {
      resolve(dgIndex) {
        if (dgIndex == null || Number.isNaN(dgIndex)) {
          return defaultRole || 'patient';
        }
        if (byIndex.has(dgIndex)) return byIndex.get(dgIndex);
        let role;
        if (!firstSeen) {
          role = claim(defaultRole || 'doctor');
          firstSeen = true;
        } else {
          role = claim(ROLE_ORDER[byIndex.size] || 'family');
        }
        byIndex.set(dgIndex, role);
        return role;
      },
    };
  }

  // ── AnalyserNode → RAF level loop (unchanged from the Deepgram engine) ──
  function attachAnalyser(stream, onLevel) {
    const AC = g.AudioContext || g.webkitAudioContext;
    if (!AC) return { stop() {} };
    const ctx = new AC();
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    src.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    let raf;
    let alive = true;
    function tick() {
      if (!alive) return;
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const avg = sum / data.length / 255;
      try { onLevel && onLevel(Math.min(1, avg * 1.6)); } catch (e) {}
      raf = g.requestAnimationFrame(tick);
    }
    tick();
    return {
      stop() {
        alive = false;
        cancelAnimationFrame(raf);
        try { src.disconnect(); } catch (e) {}
        try { ctx.close(); } catch (e) {}
      },
    };
  }

  // ── Session ──────────────────────────────────────────────────────
  function create(opts = {}) {
    const o = Object.assign({
      encounterId: null,
      sessionType: 'opd-intake',
      speaker: 'Patient',
      lang: (g.OPDBridge?.lang) || 'en',
      dedup: true,           // merge any re-sent near-duplicate finals
      diarize: undefined,
      onLevel: () => {},
      onInterim: () => {},   // never called by the streaming path — see file header
      onFinal: () => {},
      onState: () => {},
      onReady: () => {},     // fires once the relay WebSocket reaches OPEN
      onUtteranceEnd: () => {},
      onSilence: () => {},   // alias of onUtteranceEnd (same fire points)
      onError: () => {},
    }, opts);

    // Resolve whether this session diarizes. Kept as a knob (see the
    // diarization-gap note above) even though it currently has no effect on
    // role resolution — dgIndex is always null regardless.
    const diarizeOn = (typeof o.diarize === 'boolean')
      ? o.diarize
      : (o.sessionType === 'opd-consult');
    const defaultRole = roleFromLabel(o.speaker);
    let roleResolver = createRoleResolver(defaultRole);

    let state = 'idle';
    let stream = null;
    let mic = null;          // MicCapture instance (lib/audio.js)
    let analyser = null;
    let ws = null;
    let pcmChunks = [];       // Uint8Array PCM16 blocks, accumulated for the WAV fallback/playback
    let finalSegments = [];
    let useStreaming = true;
    let startedAtMs = 0;
    let wsOpened = false;
    // PCM chunks captured before the relay socket reaches OPEN. Capture
    // starts immediately (so the first words are never lost) and these are
    // flushed, in order, the moment the socket opens.
    let preOpenQueue = [];
    let lastFinalMs = 0;
    let runId = 0;
    let cancelled = false;
    let drainResolve = null;

    function setState(s) { state = s; try { o.onState(s); } catch (e) {} }

    let signalledThisPause = false;
    function signalEndOfSpeech() {
      try { o.onUtteranceEnd(); } catch (e) {}
      try { o.onSilence(); } catch (e) {}
    }

    function commitFinal(text) {
      const t = (text || '').trim();
      if (!t) return;
      const nowMs = Date.now();
      // dgSpeaker is always null (see the diarization-gap note) — resolve()
      // falls back to defaultRole every time.
      const role = diarizeOn ? roleResolver.resolve(null) : defaultRole;
      const speakerLabel = ROLE_LABEL[role] || o.speaker;
      const prev = finalSegments[finalSegments.length - 1];
      if (
        o.dedup && prev &&
        prev.role === role &&
        (nowMs - lastFinalMs) <= 6000 &&
        isNearDuplicate(prev.text, t)
      ) {
        if (t.length > prev.text.length) prev.text = t;
        lastFinalMs = nowMs;
        if (drainResolve) drainResolve();
        return;
      }
      finalSegments.push({ text: t, ts: new Date().toISOString(), conf: null, speaker: speakerLabel, role, dgSpeaker: null, diarized: false });
      lastFinalMs = nowMs;
      try { o.onFinal(t, { confidence: null, speaker: speakerLabel, role, dgSpeaker: null, diarized: false }); } catch (e) {}
      if (drainResolve) drainResolve();
    }

    // ─── prewarm ──────────────────────────────────────────────────
    // Open the microphone + cache a Sarvam ticket BEFORE the user taps
    // record — same shape/purpose as the old Deepgram-token prewarm, just a
    // relay ticket instead of a provider token. We deliberately don't open
    // the WebSocket here (a warm-but-idle relay socket has nothing to do and
    // Sarvam upstream sockets don't need to be pre-opened); start() opens it
    // at the exact moment it's needed.
    let warmStream = null;
    let warmTicket = null;
    let warmTicketAt = 0;
    async function prewarm() {
      if (state !== 'idle') return false;
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return false;
      let ok = true;
      try {
        if (!warmStream) {
          warmStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        }
      } catch (e) {
        ok = false;
      }
      try {
        if (!warmTicket || (Date.now() - warmTicketAt) > 40000) {
          warmTicket = await getSarvamTicket(o.surface || o.sessionType, o.token);
          warmTicketAt = Date.now();
        }
      } catch (e) {
        ok = false;
      }
      return ok;
    }

    async function start() {
      if (state !== 'idle') return false;
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setState('error');
        try { o.onError(Object.assign(new Error('INSECURE_CONTEXT: mic needs https or localhost'), { code: 'INSECURE_CONTEXT' })); } catch (e) {}
        return false;
      }
      const myRun = ++runId;
      cancelled = false;
      setState('processing');
      startedAtMs = Date.now();

      // Resolve the Sarvam relay ticket CONCURRENTLY with the mic request.
      // Reuse a prewarmed ticket when it's still fresh (tickets are minted
      // with a 45s TTL server-side; anything ≤40s old here is safe).
      const ticketPromise = (async () => {
        const warm = g.OPDVoice && g.OPDVoice.__warmTicket;
        const fresh = warm && (Date.now() - warm.at) < 40000 ? warm.tok : null;
        const localFresh = (warmTicket && (Date.now() - warmTicketAt) < 40000) ? warmTicket : null;
        const tok = fresh || localFresh || await getSarvamTicket(o.surface || o.sessionType, o.token);
        // Single-use: clear after consuming so the next session re-fetches.
        if (fresh && g.OPDVoice) g.OPDVoice.__warmTicket = null;
        return tok;
      })();
      ticketPromise.catch(() => {});

      try {
        if (warmStream && warmStream.active) {
          stream = warmStream;
          warmStream = null;
        } else {
          stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        }
      } catch (e) {
        setState('error');
        try { o.onError(Object.assign(new Error('Microphone permission denied: ' + e.message), { code: 'MIC_DENIED' })); } catch (_) {}
        return false;
      }
      if (cancelled || runId !== myRun) {
        try { mic?.stop(); } catch (e) {}
        stream?.getTracks().forEach((t) => t.stop());
        return false;
      }
      analyser = attachAnalyser(stream, o.onLevel);

      mic = new MicCapture((b64) => {
        if (cancelled || runId !== myRun || pausedFlag) return;
        const bytes = base64ToBytes(b64);
        pcmChunks.push(bytes);
        if (useStreaming) {
          if (ws && ws.readyState === WebSocket.OPEN) {
            try { ws.send(bytes); } catch (e) {}
          } else if (!wsOpened) {
            preOpenQueue.push(bytes);
          }
        }
      });
      try {
        await mic.start();
      } catch (e) {
        setState('error');
        try { o.onError(Object.assign(new Error('MEDIARECORDER_UNSUPPORTED: this browser can\'t capture audio: ' + e.message), { code: 'MEDIARECORDER_UNSUPPORTED' })); } catch (_) {}
        try { analyser?.stop(); } catch (_) {}
        stream?.getTracks().forEach((t) => t.stop());
        return false;
      }
      if (cancelled || runId !== myRun) {
        try { mic.stop(); } catch (e) {}
        try { analyser?.stop(); } catch (e) {}
        stream?.getTracks().forEach((t) => t.stop());
        return false;
      }
      setState('recording');

      // Open the relay socket the moment the ticket arrives.
      ticketPromise.then((tok) => {
        if (!useStreaming || runId !== myRun || !mic) return;
        ws = new WebSocket(wsUrl(`${VOICE_STREAM_WS_PATH}?ticket=${encodeURIComponent(tok.ticket)}`));
        ws.binaryType = 'arraybuffer';
        ws.onopen = () => { /* wait for the relay's own {type:'ready'} — Sarvam upstream may still be dialing */ };
        ws.onmessage = (ev) => {
          let m;
          try { m = JSON.parse(ev.data); } catch (e) { return; }
          if (m.type === 'ready') {
            wsOpened = true;
            if (preOpenQueue.length) {
              const queued = preOpenQueue;
              preOpenQueue = [];
              for (const bytes of queued) { try { ws.send(bytes); } catch (e) {} }
            }
            try { o.onReady(); } catch (e) {}
            return;
          }
          if (m.type === 'event') {
            if (m.signal === 'END_SPEECH') {
              if (drainResolve) drainResolve();
              if (!signalledThisPause) { signalledThisPause = true; signalEndOfSpeech(); }
            } else if (m.signal === 'START_SPEECH') {
              signalledThisPause = false;
            }
            return;
          }
          if (m.type === 'transcript') {
            signalledThisPause = false;
            commitFinal(m.text || '');
            return;
          }
          if (m.type === 'error') {
            if (!wsOpened) { useStreaming = false; }
            else { try { o.onError(new Error('Sarvam stream: ' + (m.message || 'unknown error'))); } catch (e) {} }
            if (drainResolve) drainResolve();
          }
        };
        ws.onerror = () => {
          if (!wsOpened) useStreaming = false;
        };
        ws.onclose = () => { ws = null; };
      }).catch(() => {
        // Ticket mint failed / relay not available → mic keeps buffering PCM
        // and stop() uses authenticated batch STT on the full recording.
        useStreaming = false;
      });
    }

    function pause() {
      // MicCapture has no pause/resume primitive (Web Audio graphs don't
      // suspend per-track the way MediaRecorder did) — mute the outgoing
      // stream by dropping chunks instead. The mic stays open; onLevel and
      // the relay connection are unaffected, matching the visible contract
      // (recState → 'paused') the UI expects.
      if (state === 'recording') { pausedFlag = true; setState('paused'); }
    }
    function resume() {
      if (state === 'paused') { pausedFlag = false; setState('recording'); }
    }
    let pausedFlag = false;

    async function stop() {
      if (!mic) {
        cancelled = true;
        runId++;
        try { if (warmStream) warmStream.getTracks().forEach((t) => t.stop()); } catch (e) {}
        warmStream = null;
        try { if (stream) stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
        stream = null;
        if (state !== 'idle') setState('idle');
        return { transcript: '', durationMs: 0, segments: [], diarizedTurns: [], diarizationError: null, audioBlob: null, audioUrl: null };
      }
      runId++; // invalidate any in-flight ticket→WebSocket hop from start()
      setState('processing');
      try { mic.stop(); } catch (e) {}

      // Drain Sarvam: send flush and WAIT for a transcript/event/error (or
      // the 2s cap) instead of a fixed sleep — mirrors the old Deepgram
      // Finalize-drain, same reasoning (don't cut off trailing words).
      if (wsOpened && ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'flush' })); } catch (e) {}
        await new Promise((resolve) => {
          const cap = setTimeout(() => { drainResolve = null; resolve(); }, 2000);
          drainResolve = () => { clearTimeout(cap); drainResolve = null; resolve(); };
        });
        try { ws.close(); } catch (e) {}
      } else if (ws) {
        try { ws.close(); } catch (e) {}
      }
      if (analyser) analyser.stop();
      if (stream) stream.getTracks().forEach((t) => { try { t.stop(); } catch (e) {} });

      let transcript = finalSegments.map((s) => s.text).join(' ').trim();
      let durationMs = Math.max(0, Date.now() - startedAtMs);
      const audioBlob = pcmChunks.length ? pcmChunksToWavBlob(pcmChunks, 16000) : null;
      const audioUrl = audioBlob ? URL.createObjectURL(audioBlob) : null;
      let transcriptionError = null;
      let diarizedTurns = [];
      let diarizationError = null;

      // POST-SESSION DIARIZATION (multi-party consult sessions only): one
      // whole-recording batch call re-attributes who-said-what — the relay
      // stream carries no speaker signal, so live bubbles run on the UI
      // toggle and this pass corrects attribution after Stop (seconds-fast;
      // see server/stt/sarvam.mjs). The SAME call doubles as the transcript
      // fallback when streaming produced no finals — never two transcriptions
      // of one recording. Attribution failure is deliberately non-fatal when
      // a streaming transcript exists (the capture must never be lost over an
      // attribution nicety); it IS fatal when there is no transcript at all.
      if (audioBlob && (diarizeOn || !transcript)) {
        try {
          const batch = await transcribeRecordedBlob(audioBlob, {
            magicLinkToken: o.token,
            sessionType: o.sessionType,
            speaker: o.speaker,
            diarize: diarizeOn,
          });
          if (diarizeOn) {
            // <2 distinct voices ⇒ [] — the live toggle stays authoritative
            // for single-speaker sessions (canonical rule, speech-turns.js).
            diarizedTurns = utterancesToClinicalTurns(batch.utterances);
          }
          if (!transcript) {
            transcript = batch.transcript;
            durationMs = batch.durationMs || durationMs;
            if (transcript) try { o.onFinal(transcript, { confidence: batch.confidence, speaker: o.speaker, role: defaultRole, dgSpeaker: null, diarized: false, fallback: 'authenticated-batch' }); } catch (e) {}
          }
        } catch (error) {
          if (!transcript) {
            transcriptionError = error;
            setState('error');
            try { o.onError(error); } catch (e) {}
          } else {
            diarizationError = error;
            try { console.warn('[opd-voice] post-session diarization failed; keeping live speaker attribution', error); } catch (e) {}
          }
        }
      }

      if (!transcriptionError) setState('done');
      const out = {
        transcript,
        durationMs,
        segments: finalSegments,
        diarizedTurns,
        diarizationError,
        transcriptionError,
        audioBlob,
        audioUrl,
      };
      // Reset for next use
      mic = null; ws = null; analyser = null; stream = null; pcmChunks = []; finalSegments = []; startedAtMs = 0; state = 'idle';
      preOpenQueue = []; lastFinalMs = 0; wsOpened = false; useStreaming = true; drainResolve = null;
      cancelled = false; pausedFlag = false;
      signalledThisPause = false;
      roleResolver = createRoleResolver(defaultRole);
      return out;
    }

    // Explicit fresh-start: wipe all in-memory capture state. Refuses while
    // a capture is live unless `force`.
    function reset({ force = false } = {}) {
      if (!force && (state === 'recording' || state === 'paused')) return false;
      runId++;
      try { if (analyser) analyser.stop(); } catch (e) {}
      try { if (stream) stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
      try { if (warmStream) warmStream.getTracks().forEach((t) => t.stop()); } catch (e) {}
      warmStream = null;
      try { if (mic) mic.stop(); } catch (e) {}
      try { if (ws && ws.readyState === WebSocket.OPEN) ws.close(); } catch (e) {}
      mic = null; ws = null; analyser = null; stream = null;
      pcmChunks = []; finalSegments = [];
      preOpenQueue = []; lastFinalMs = 0; startedAtMs = 0;
      wsOpened = false; useStreaming = true; drainResolve = null;
      cancelled = false; pausedFlag = false;
      signalledThisPause = false;
      roleResolver = createRoleResolver(defaultRole);
      setState('idle');
      return true;
    }

    return {
      start, pause, resume, stop, prewarm, reset,
      get state() { return state; },
      get pendingInterim() { return ''; }, // Sarvam has no intra-utterance interim — see file header
    };
  }

  g.OPDVoice = { create, getSarvamTicket, transcribeRecordedBlob, isNearDuplicate, roleFromLabel, createRoleResolver, ROLE_LABEL, ROLE_ORDER };
})(typeof window !== 'undefined' ? window : globalThis);
